import { ReactNode } from 'react'

// Components
import Label, { OnlyLarge } from 'components/filter/filters/label/label'
import MultitrackFilter from 'components/filter/searchandsort/multitrackfilter'

// Types
import { QueryObject, SearchConfig, Genre, Region, TrackType, SearchConfigBucket } from 'types'

// Utils
import { KEYS_CHOICES, CATEGORIES_CHOICES } from '@constants'
import styles from './filters.css'

type CheckFilterOptions = Array<{ id: number | string; name: string }>

type AllFilters = {
  id: string
  datatesting: string
  label: ReactNode
}
type CheckFilter = AllFilters & {
  options: CheckFilterOptions
  selected?: Array<string> | Array<number>
  header?: ReactNode
  listClassName?: string
  selectedOptionsCount?: number
}
type SliderFilter = AllFilters & {
  type: 'slider'
  min: number
  max: number
  values: [number, number]
  labelSeparation?: number
}

export const getFilters = (
  query: QueryObject,
  searchConfig: SearchConfig,
  genreOptions: Array<Genre>,
  regionOptions: Array<Region>,
  trackTypeOptions: Array<TrackType>,
  queryObj?: QueryObject,
): {
  genres: CheckFilter
  keys: CheckFilter
  types: CheckFilter
  bpm: SliderFilter
  release_year: SliderFilter
  regions: CheckFilter
  categories: CheckFilter
} => {
  const {
    genres: selectedGenres,
    regions: selectedRegions,
    keys: selectedKeys,
    types: selectedTypes,
    categories: selectedCategories,
    bpm_over: selectedBpmOver,
    bpm_under: selectedBpmUnder,
    release_year_after: selectedYearAfter,
    release_year_before: selectedYearBefore,
    multitrack,
  } = query

  const isGenrePage = !!queryObj?.genres

  const getOptionsForFilter = (
    id: 'genres' | 'types' | 'regions' | 'tonal' | 'license_category',
    allOptions: Array<Genre> | Array<TrackType> | Array<Region> | Array<{ id: number | string; name: string }>,
  ): CheckFilterOptions => {
    if (!searchConfig) {
      return [{ id: 0, name: 'Loading…' }]
    }
    const buckets = searchConfig?.[id as keyof SearchConfig].buckets as Array<SearchConfigBucket>
    const options = buckets
      .map(({ key }) => allOptions.find(option => String(option[(id === 'genres' ? 'slug' : 'id') as keyof typeof option]) === String(key)))
      .filter(option => option !== undefined)

    if (id === 'genres') {
      const currentGenreId = genreOptions.find(genre => genre.slug === queryObj?.genres?.[0])?.id
      return (options as Array<Genre>)
        .filter((genre: Genre) => (isGenrePage ? genre.parent === currentGenreId : !genre.parent)) // show children on genre pages but not other pages
        .map((genre: Genre) => ({ id: genre.slug, name: genre.name }))
    }

    return options as CheckFilterOptions
  }

  const genres = getOptionsForFilter('genres', genreOptions).sort((a, b) => a.name.localeCompare(b.name))
  const types = getOptionsForFilter('types', trackTypeOptions).sort((a, b) => a.name.localeCompare(b.name))
  const regions = getOptionsForFilter('regions', regionOptions)
  const keys = getOptionsForFilter('tonal', KEYS_CHOICES).sort((a, b) => a.name.localeCompare(b.name))
  const licenseCategories = getOptionsForFilter('license_category', CATEGORIES_CHOICES).sort((a, b) => a.name.localeCompare(b.name))
  const minBPM = searchConfig?.min_bpm.value
  const maxBPM = searchConfig?.max_bpm.value
  const minYear = searchConfig?.min_year.value
  const maxYear = searchConfig?.max_year.value

  return {
    genres: {
      id: 'genres',
      options: genres,
      selected: selectedGenres,
      datatesting: 'dropdown_genre',
      label: <Label>{isGenrePage ? 'Sub-genres' : 'Genres'}</Label>,
    },
    keys: {
      id: 'keys',
      options: keys,
      selected: selectedKeys,
      datatesting: 'dropdown_key',
      label: (
        <Label>
          <>
            <OnlyLarge>Song</OnlyLarge>keys
          </>
        </Label>
      ),
    },
    types: {
      id: 'types',
      options: types,
      selected: selectedTypes,
      datatesting: 'dropdown_track_type',
      label: (
        <Label>
          <>
            <OnlyLarge>Track</OnlyLarge>types
          </>
        </Label>
      ),
      // extra props to add "multitrack option" to dropdown
      header: types.length > 1 ? <MultitrackFilter /> : null,
      listClassName: styles.typesList,
      selectedOptionsCount: [...(selectedTypes ? [...selectedTypes] : []), ...(multitrack ? [{}] : [])].length,
    },
    bpm: {
      id: 'bpm',
      type: 'slider',
      min: minBPM as number,
      max: maxBPM as number,
      values: [(selectedBpmOver || minBPM) as number, (selectedBpmUnder || maxBPM) as number],
      datatesting: 'dropdown_bpm',
      labelSeparation: 4,
      label: <Label>BPM</Label>,
    },
    release_year: {
      id: 'release_year',
      type: 'slider',
      min: minYear as number,
      max: maxYear as number,
      values: [(selectedYearAfter || minYear) as number, (selectedYearBefore || maxYear) as number],
      datatesting: 'dropdown_release_year',
      label: (
        <Label>
          <>
            <OnlyLarge>Release</OnlyLarge>year
          </>
        </Label>
      ),
    },
    regions: {
      id: 'regions',
      options: regions,
      selected: selectedRegions,
      datatesting: 'dropdown_region',
      label: <Label>Region</Label>,
    },
    categories: {
      id: 'categories',
      options: licenseCategories,
      selected: selectedCategories,
      datatesting: 'dropdown_categories',
      label: (
        <Label>
          <>
            <OnlyLarge>License</OnlyLarge>category
          </>
        </Label>
      ),
    },
  }
}
